import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { storeFromHref, type StoreKind } from "../helpers";
import { Icon, type IconName } from "../icons";
import { CARD_HOVER, Container, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Item = SectionProps<"links">["node"]["content"]["items"][number];

export const STORE_ICONS: Record<StoreKind, IconName> = {
  shop: "shopping-bag",
  tiktok: "tiktok",
  instagram: "instagram",
  youtube: "youtube",
  facebook: "facebook",
  whatsapp: "whatsapp",
  support: "heart",
  music: "music",
  email: "mail",
  phone: "phone",
  link: "link",
};

function SponsoredBadge({ light }: { light?: boolean }) {
  return (
    <span className={cx("shrink-0 rounded-full border px-2 py-0.5 text-[0.6875rem] font-semibold tracking-wide uppercase", light ? "border-site-on-primary/40 text-site-on-primary" : "border-site-border text-site-muted")}>
      Afiliasi
    </span>
  );
}

function LinkItem({ ctx, item, i, grid }: { ctx: RenderContext; item: Item; i: number; grid: boolean }) {
  const store = storeFromHref(item.href);
  const hi = !!item.highlight;
  const p = `content.items.${i}`;
  const isWa = store.kind === "whatsapp";
  const tone = hi ? "border-transparent bg-site-primary text-site-on-primary shadow-site-md" : "border-site-border bg-site-surface text-site-text shadow-site-sm hover:border-site-primary/40";
  return (
    <SiteLink
      ctx={ctx}
      href={item.href}
      whatsapp={isWa}
      trackLink={!isWa}
      sponsored={item.sponsored}
      className={cx(
        "group flex w-full items-center gap-4 rounded-site-md border text-left",
        grid ? "h-full min-h-24 p-5" : "min-h-16 px-4 py-3 @md:px-5",
        tone,
        CARD_HOVER,
      )}
    >
      <span
        aria-hidden="true"
        className={cx("grid size-11 shrink-0 place-items-center rounded-full transition-transform duration-300 group-hover:scale-110 motion-reduce:transition-none", hi ? "bg-site-on-primary/15" : "bg-site-primary/10 text-site-primary")}
      >
        <Icon name={STORE_ICONS[store.kind]} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-semibold leading-snug" {...field(ctx, `${p}.label`)}>
            {item.label}
          </span>
          {item.sponsored ? <SponsoredBadge light={hi} /> : null}
        </span>
        {item.description ? (
          <span className={cx("mt-0.5 block text-sm leading-snug", hi ? "text-site-on-primary/85" : "text-site-muted")} {...field(ctx, `${p}.description`)}>
            {item.description}
          </span>
        ) : null}
      </span>
      <Icon name="arrow-up-right" className={cx("size-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none", hi ? "opacity-90" : "text-site-muted")} />
    </SiteLink>
  );
}

/** Big tappable link buttons: the heart of a link-in-bio page (shops, videos, tips, contact). */
export function Links({ node, ctx }: SectionProps<"links">) {
  const c = node.content;
  const grid = node.variant === "grid";
  const hasHeader = !!(c.title || c.eyebrow);
  const anySponsored = c.items.some((it) => it.sponsored);
  return (
    // Without a heading the buttons sit right under the profile hero, so the top gap stays small.
    <SectionShell ctx={ctx} node={node} labelledBy={!!c.title} className={hasHeader ? undefined : "pt-4 @3xl:pt-6"}>
      <Container size={grid ? "default" : "narrow"}>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
        <ul className={cx(hasHeader && STACK, STAGGER, grid ? "grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-3" : "mx-auto flex max-w-md flex-col gap-3")}>
          {c.items.map((item, i) => (
            <li key={i}>
              <LinkItem ctx={ctx} item={item} i={i} grid={grid} />
            </li>
          ))}
        </ul>
        {c.items.length === 0 && ctx.editor ? <p className="rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted">Tambahkan tautan.</p> : null}
        {anySponsored ? <p className="mx-auto mt-5 max-w-md text-center text-xs text-site-muted">Tautan bertanda Afiliasi dapat memberi saya komisi, tanpa biaya tambahan untukmu.</p> : null}
      </Container>
    </SectionShell>
  );
}
